import { IPageHeaderTab } from '@misc/page-header/page-header.component';

import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';

import { FleetCardsTransactionStatuses, FleetCardsTransactionDateTypes } from '@lib/i18n-backend-strings';
import { BLUE_90, BLUE_70, BLUE_50, BLUE_30, BLUE_60, YELLOW_20, GREY_20 } from '@lib/colors';
import { PhxSelectOption } from 'phx-core/lib/select/select.types';
import {
  ICategoryRow,
  ITransactionDrawerConfig,
  ITransactionAppliedSettings,
} from './transactions/transactions.interface';
import { EntityState } from '@lib/entity.const';

export const MOMENT_TIME_FORMAT = 'MMM DD, hh:mm a';

export const GALLONS_PER_LITER: number = 0.264172;

export const FLEET_CARDS_URL_PREFIX: string = 'fleet-cards';
export const UPSELL_URL_PREFIX: string = 'upsell';

export const FLEET_CARDS_TITLE: string = $localize`Cards`;
export const FLEET_CARDS_TITLE_UPSELL_V2: string = $localize`Motive Card`;

export const UN_LOCKED_CARD_TEXT = $localize`Card is not available for use.`;
export const DEACTIVATED_CARD_TEXT = $localize`Card can no longer be used to make purchases.`;
export const ACTIVE_CARD_TEXT = $localize`Card is available for use.`;
export const FROZEN_CARD_TEXT = $localize`Card is temporarily disabled.`;
export const LOCKED_VEHICLE_CARD_TITLE = $localize`Lock card?`;
export const DECLINE_REASON_CUSTOM_FIELD_NOT_ASSIGNED_METATA = 'custom_field_not_assigned';
export const DECLINE_REASON_CARD_DEACTIVATED = 'declined_by_marqeta_1813';
export const DECLINE_REASON_CARD_READER_ISSUE = 'declined_by_marqeta_1825_custom';
export const ODOMETER_CONFIG_ID = 'odometer_config_id';
export const VEHICLE_CONFIG_ID = 'vehicle_config_id';
export const ASSET_CONFIG_ID = 'asset_config_id';
export const RECEIPT_CONFIG_ID = 'txn_receipt_id';
export const COMMENT_CONFIG_ID = 'txn_comment_id';

export enum CARD_STATUS {
  active = 'active',
  active_locked = 'active_locked',
  active_unlocked = 'active_unlocked',
  unassigned = 'unassigned',
  assigned = 'assigned',
  inactive = 'inactive',
  frozen = 'frozen',
  suspended = 'suspended',
  terminated = 'terminated',
}

export enum TRANSACTION_STATUS {
  completed = 'completed',
  reversed = 'reversed',
  pending = 'pending',
  refundPending = 'refund_pending',
  refundReversed = 'refund_reversed',
  refundDeclined = 'refund_declined',
  declined = 'declined',
  credit = 'credit',
  disputed = 'disputed',
  posted = 'posted',
  error = 'error',
  refund = 'refund',
  disputeClosed = 'dispute_closed',
}

export enum TRANSACTION_DRAWER_TABS {
  Overview,
  Dispute,
}

export enum TRANSACTION_DRAWER_SOURCES {
  MyCard = 'MY_CARD',
  TransactionsPage = 'TRANSACTION_PAGE',
}

// to show-hide sections/rows in transaction details drawer
export const TRANSACTION_DRAWER_CONFIG: {[key: string]: ITransactionDrawerConfig} = {
  [TRANSACTION_DRAWER_SOURCES.MyCard]: {
    merchant: { show: true, showBlockedCTA: false },
    card: { show: true },
    notes: { show: false },
    status: { show: true },
    amount: { show: false },
    metaData: {
      show: true,
      ignoreTransactionManagePermission: true,
    },
    category: { show: true },
    cardholder: { show: false },
    postedDate: { show: true },
    declineReason: { show: true },
    purchaseDate: { show: true },
    missedSavings: { show: false },
    transactionId: { show: false },
    itemizedReceipt: { show: true },
    merchantControl: { show: false },
    fraudProtectionGuarantee: { show: false },
    transactionIrregularities: { show: false },
  },
  [TRANSACTION_DRAWER_SOURCES.TransactionsPage]: {
    merchant: { show: true, showBlockedCTA: false },
    card: { show: true },
    notes: { show: true },
    status: { show: true },
    amount: { show: true },
    metaData: {
      show: true,
      ignoreTransactionManagePermission: false,
    },
    category: { show: true },
    postedDate: { show: true },
    cardholder: { show: true },
    declineReason: { show: true },
    purchaseDate: { show: true },
    missedSavings: { show: true },
    transactionId: { show: true },
    itemizedReceipt: { show: true },
    merchantControl: { show: true },
    fraudProtectionGuarantee: { show: true },
    transactionIrregularities: { show: true },
  },
};

export enum TRANSACTION_DATE_TYPE {
  purchaseDate = 'transaction_time',
  postedDate = 'posted_at',
}

export enum THEFT_REVIEW_STATUS {
  reviewed = 'reviewed',
  needsReview = 'needs_review',
  irregularityConfirmed = 'irregularity_confirmed',
  incorrectlyFlagged = 'incorrectly_flagged',
}

export enum POST_TXN_RECEIPT {
  receiptRequired = 'receipt_required',
}

export enum CATEGORIES_FILTER_KEYS {
  cash = 'cash',
  gasoline = 'gasoline',
  diesel = 'diesel',
  misc = 'misc',
  miscFuel = 'misc fuel',
  maintenance = 'maintenance',
  tires = 'tires',
  carWashes = 'car washes',
  parkingAndTolls = 'parking & tolls',
  food = 'food',
  towing = 'towing',
  travel = 'travel',
  other = 'other',
  def = 'def',
  reefer = 'reefer fuel',
  merchantDiscount = 'merchant discount',
  advertising = 'advertising',
  airTravel = 'air travel',
  booksAndnewspapers = 'books & newspapers',
  busesAndTrains = 'buses and trains',
  businessAndprofessionalServices = 'business & professional services',
  carrRentals = 'car rentals',
  clubsAndmemberships = 'clubs & memberships',
  computerHardwareAndSoftware = 'computer hardware and software',
  electricVehicleCharging = 'electric vehicle charging',
  entertainment = 'entertainment',
  fuelStationPurchases = 'fuel station purchases',
  governmentServices = 'government services',
  hardwareStores = 'hardware stores',
  insurance = 'insurance',
  internetAndPhoneServices = 'internet & phone services',
  lodging = 'lodging',
  medical = 'medical',
  miscNonFuel = 'misc. (non-fuel)',
  officeSupplies = 'office supplies',
  supermarketsAndGeneralMerchandise = 'supermarkets & general merchandise',
  taxisAndRideshares = 'taxis and rideshares',
  trainingAndEducation = 'training and education',
  travelExpenses = 'travel expenses',
  utilities = 'utilities (electric, gas, etc.)',
}

export const MERCHANT_DISCOUNT_CATEGORY: string = 'Merchant discount';

export enum FEE_TYPES_KEYS {
  monthly_fee = 'monthly_fee',
  late_fee = 'late_fee',
}

export enum DISCOUNT_IDENTIFIER {
  doubleDiscount = 'Double Discount',
  directDiscount = 'Direct Discount',
  bonusRebates = 'Bonus Rebates',
}

export enum PRODUCT_TYPE {
  misc = 'misc',
  diesel = 'diesel',
  gasoline = 'gasoline',
  maintenance = 'maintenance',
  miscFuel = 'misc fuel',
}

export enum REBATE_STATUSES {
  completed = 'completed',
  pending = 'pending',
  forfeited = 'forfeited',
}

export interface IDayOfWeek {
  id: string;
  label: string;
  shortLabel: string;
  selected: boolean;
}

export interface ITransactionDetailsMerchant {
  storeId: string;
  brandName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  lon?: number;
  lat?: number;
  ppgInDollars?: number;
  productType?: string;
}

export interface ITransactionDetails {
  recommendedMerchant: ITransactionDetailsMerchant;
  sourceMerchant: ITransactionDetailsMerchant;
  routeInfo: Array<[number,number]>;
  distanceInMiles: number;
  missedSavingsInDollars: number;
  appliedSettings?: ITransactionAppliedSettings;
  fraudProtectionSettings?: IFraudProtectionSettings;
}

export interface IFraudProtectionSetting {
  fraudProtectionSetting: string;
  enabled: boolean;
  applied: boolean;
  notAppliedReason: string | null;
  reasonMetadata?: IReasonMetadata;
}

export interface IReasonMetadata {
  updatedAt: string;
  updatedBy: string;
}

export interface IFraudProtectionSettings {
  mobileBasedUnlock: IFraudProtectionSetting;
  vehicleProximityBasedDecline: IFraudProtectionSetting;
  fuelLevelExceedsLimitSettings: IFraudProtectionSetting;
  spendControl: IFraudProtectionSetting;
  merchantControl: IFraudProtectionSetting;
}

export type CardStatus = keyof typeof CARD_STATUS;

export enum TABS {
  Overview = 'overview',
  Cardholders = 'cardholders',
  Transactions = 'transactions',
  Savings = 'savings',
  Benefits = 'benefits',
  Apply = 'apply',
}

export enum FLEETCARDS_DISPUTE_STATUSES {
  opened = 'opened',
  disputeOpened = 'disputeOpened',
  disputeClosed = 'disputeClosed',
  initiated = 'initiated',
  disputed = 'disputed',
  lost = 'lost',
  won = 'won',
  partiallyLost = 'partially_lost',
  partiallyWon = 'partially_won',
  closed = 'closed',
  cancelled = 'cancelled',
}

export enum REBATE_OFFERS {
  '5 Cents Off' = '5_cents_off',
  '3 Cents Off' = '3_cents_off',
  '0 Cents Off' = '0_cents_off',
}

export enum FEEDBACK_REASON_LIST {
  INACCURATE_TELEMATICS = 'inaccurate_telematics_info',
  INACCURATE_TRANSACTION_INFO = 'inaccurate_transaction_info',
  FALSE_POSITIVE = 'user_selected_false_positive',
  INACCURATE_MERCHANT_LOCATION_INFO = 'inaccurate_merchant_location_info',
  INACCURATE_VEHICLE_LOCATION_INFO = 'inaccurate_vehicle_location_info',
  OTHER = 'other',
}

export const AUTO_DECLINE_FEEDBACK_REASON_LIST: { [key: string]: string } = {
  OTHER: $localize`Other`,
  FUEL_TANK_LEVEL_WAS_INCORRECT_AT_THE_TIME_OF_TRANSACTION: $localize`Fuel tank level was incorrect at the time of transaction`,
};

export enum AUTO_DECLINE_FEEDBACK_FORM_FIELD_NAMES {
  reason = 'reason',
  otherComment = 'otherComment',
}

export const FEEDBACK_REASON_VALUE = {
  inaccurate_telematics_info: $localize`The telematics info is inaccurate`,
  inaccurate_transaction_info: $localize`The transaction details are inaccurate`,
  user_selected_false_positive: $localize`There\'s nothing wrong with this transaction`,
  inaccurate_merchant_location_info: $localize`Merchant location was incorrect`,
  inaccurate_vehicle_location_info: $localize`Vehicle location was incorrect`,
};

export enum REVIEWED_FRAUD_ACTION_LIST {
  APPROVE = 'approve',
  FREEZE = 'freeze',
  TERMINATE = 'terminate',
}

export const CARD_AUTO_FROZEN_REASONS = ['avs_failed', 'declined_by_marqeta_1874', 'declined_by_marqeta_1915', 'high_fraud_risk_score'];

export const CATEGORY_SPECIFIC_DECLINE_REASON = [
  'category_billing_cycle_credit_exceeded',
  'category_maximum_transaction_amount_exceeded',
  'category_day_limit_exceeded',
  'category_week_limit_exceeded',
  'category_month_limit_exceeded',
];

export const AVAILABLE_LIMIT_DECLINED_REASONS_MAPPING = {
  day_limit_exceeded: 'Day spend limit reached.',
  week_limit_exceeded: 'Weekly spend limit reached.',
  month_limit_exceeded: 'Monthly spend limit reached.',
};

export const FLEET_CARDS_LIST_VIEW_URLS: string[] = [
  `${FLEET_CARDS_URL_PREFIX}/${TABS.Cardholders}`,
  `${FLEET_CARDS_URL_PREFIX}/${TABS.Transactions}`,
  `${FLEET_CARDS_URL_PREFIX}/${TABS.Overview}`,
];

export const FLEET_CARDS_NO_SHADOW_URLS: string[] = [
  `${FLEET_CARDS_URL_PREFIX}/${TABS.Cardholders}`,
  `${FLEET_CARDS_URL_PREFIX}/${TABS.Transactions}`,
  `${FLEET_CARDS_URL_PREFIX}/${TABS.Savings}`,
];


export function getUpsellFleetCardTabs(featureFlagService: FleetCardsFeatureFlagService): IPageHeaderTab[] {
  return [
    {
      label: $localize`Apply`,
      link: `/${FLEET_CARDS_URL_PREFIX}/${UPSELL_URL_PREFIX}/${TABS.Apply}`,
      value: TABS.Apply,
      seperator: false,
      shouldNavItemShow: true,
      isNew: false,
    },
    {
      label: $localize`Benefits`,
      link: `/${FLEET_CARDS_URL_PREFIX}/${UPSELL_URL_PREFIX}/${TABS.Benefits}`,
      value: TABS.Benefits,
      seperator: true,
      shouldNavItemShow: false,
      isNew: false,
    },
  ];
}

export function getUpsellFleetCardTabsV2(featureFlagService: FleetCardsFeatureFlagService): IPageHeaderTab[] {
  return [
    {
      label: $localize`Benefits`,
      link: `/${FLEET_CARDS_URL_PREFIX}/${UPSELL_URL_PREFIX}/${TABS.Benefits}`,
      value: TABS.Benefits,
      seperator: false,
      shouldNavItemShow: true,
      isNew: false,
    },
    {
      label: $localize`Apply`,
      link: `/${FLEET_CARDS_URL_PREFIX}/${UPSELL_URL_PREFIX}/${TABS.Apply}`,
      value: TABS.Apply,
      seperator: true,
      shouldNavItemShow: true,
      isNew: false,
    },
  ];
}

export const MESSAGES = {
  UPDATE_SPEND_CONTROL_SUCCESS: $localize`Card spend control successfully updated`,
  UPDATE_SPEND_CONTROL_ERROR: $localize`Something went wrong`,
  CREATE_SPEND_CONTROL_SUCCESS: $localize`Card spend control successfully created`,
  NOT_EMPTY: $localize`cannot be empty`,
  SHOULD_BE_NUMBER: $localize`should be a number`,
  VALUE_GREATER_THAN_ZERO: $localize`Value must be greater than zero`,
  INVALID: $localize`is invalid`,
  APPLICATION_DECLINED: $localize`Your application was recently declined. Please re-apply after 90 days.`,
  FIELD: $localize`Field`,
  INFO_IS_REQUIRED: $localize`This info is required`,
  CONSENT: $localize`Please read and agree to Motive’s Expense Management Services Agreement and Card Program Agreement by checking the box.`,
  FLEET_CARDS_GENERAL_ERROR: $localize`Something went wrong. Please try again later.`,
  FLEET_CARDS_PLAID_REAUTH_ERROR: $localize`Plaid could not verify your bank account. Try again.`,
  CARD_APPLICATION_INVALID_ERROR: $localize`Please correct the highlighted section(s)`,
  CARD_APPLICATION_INVALID_SSN_ERROR: $localize`Social Security Number (SSN) is invalid`,
  CARD_APPLICATION_INELIGIBLE_TO_APPLY_ERROR: $localize`Ineligible to apply.`,
  CARD_APPLICATION_BANK_ACCOUNT_ERROR: $localize`Please either link your bank account or upload bank statements.`,
  CARD_APPLICATION_VIRTUAL_ACCOUNT_ERROR: $localize`Please upload bank statements.`,
  CARD_APPLICATION_NO_BUSINESS_OWNER_ERROR: $localize`Add at least 1 business owner`,
  PO_BOX_NOT_ALLOWED_IN_ADDRESS: $localize`Please enter a physical address.`,
  SOLE_PROPRIETORSHIP_BUSINESS_TYPE_NOT_SUPPORTED: $localize`We are unable to support sole proprietorships at this time.`,
  CARD_NOT_FOUND: $localize`Card not found.`,
  CARDHOLDER_STATUS_CHANGE_SUCCESS: $localize`Status successfully updated`,
  CARDHOLDER_STATUS_CHANGE_ERROR: $localize`Something went wrong`,
  CARD_CHANGE_LOCK_SUCCESS: $localize`Card status updated`,
  CARD_BULK_FREEZE_UNFREEZE_SUCCESS: $localize`Card status updated`,
  CARD_BULK_FREEZE_UNFREEZE_ERROR: $localize`Something went wrong. Please try again.`,
  TEMPORARY_OVERRIDE_DOES_NOT_EXISTS: $localize`This temporary override doesn’t exist anymore`,
  SOMETHING_WENT_WRONG_ERROR: $localize`Something went wrong`,
};

export const TRANSACTION_STATUS_DROPDOWN = [
  {
    label: FleetCardsTransactionStatuses.pending,
    key: TRANSACTION_STATUS.pending,
  },
  {
    label: FleetCardsTransactionStatuses.posted,
    key: TRANSACTION_STATUS.posted,
  },
  {
    label: FleetCardsTransactionStatuses.reversed,
    key: TRANSACTION_STATUS.reversed,
  },
  {
    label: FleetCardsTransactionStatuses.refund,
    key: TRANSACTION_STATUS.refund,
  },
  {
    label: FleetCardsTransactionStatuses.declined,
    key: TRANSACTION_STATUS.declined,
  },
  {
    label: FleetCardsTransactionStatuses.disputed,
    key: TRANSACTION_STATUS.disputed,
  },
];

export const TRANSACTION_STATUS_DROPDOWN_V2 = [
  {
    label: FleetCardsTransactionStatuses.pending,
    key: TRANSACTION_STATUS.pending,
  },
  {
    label: FleetCardsTransactionStatuses.posted,
    key: TRANSACTION_STATUS.posted,
  },
  {
    label: FleetCardsTransactionStatuses.reversed,
    key: TRANSACTION_STATUS.reversed,
  },
  {
    label: FleetCardsTransactionStatuses.refund,
    key: TRANSACTION_STATUS.refund,
  },
  {
    label: FleetCardsTransactionStatuses.declined,
    key: TRANSACTION_STATUS.declined,
  },
  {
    label: FleetCardsTransactionStatuses.disputeOpen,
    key: TRANSACTION_STATUS.disputed,
  },
  {
    label: FleetCardsTransactionStatuses.disputeClosed,
    key: TRANSACTION_STATUS.disputeClosed,
  },
];

export const TRANSACTION_STATUS_DROPDOWN_PHX = [
  {
    label: FleetCardsTransactionStatuses.pending,
    value: TRANSACTION_STATUS.pending,
  },
  {
    label: FleetCardsTransactionStatuses.posted,
    value: TRANSACTION_STATUS.posted,
  },
  {
    label: FleetCardsTransactionStatuses.reversed,
    value: TRANSACTION_STATUS.reversed,
  },
  {
    label: FleetCardsTransactionStatuses.refund,
    value: TRANSACTION_STATUS.refund,
  },
  {
    label: FleetCardsTransactionStatuses.declined,
    value: TRANSACTION_STATUS.declined,
  },
  {
    label: FleetCardsTransactionStatuses.disputeOpen,
    value: TRANSACTION_STATUS.disputed,
  },
  {
    label: FleetCardsTransactionStatuses.disputeClosed,
    value: TRANSACTION_STATUS.disputeClosed,
  },
];

export const TRANSACTION_DATE_DROPDOWN = [
  {
    label: FleetCardsTransactionDateTypes.purchase_date,
    key: TRANSACTION_DATE_TYPE.purchaseDate,
  },
  {
    label: FleetCardsTransactionDateTypes.posted_date,
    key: TRANSACTION_DATE_TYPE.postedDate,
  },
];

export const TRANSACTION_DATE_ADDON_OPTIONS = {
  [TRANSACTION_DATE_TYPE.purchaseDate]: $localize`Purchase date`,
  [TRANSACTION_DATE_TYPE.postedDate]: $localize`Posted date`,
};

export const CARD_STATUS_DROPDOWN = [
  {
    label: $localize`All cards`,
    key: `${CARD_STATUS.active},${CARD_STATUS.suspended}`,
  },
  {
    label: $localize`Active cards`,
    key: CARD_STATUS.active,
  },
  {
    label: $localize`Frozen cards`,
    key: CARD_STATUS.suspended,
  },
];

export const CARD_STATUS_DROPDOWN_PHX = [
  {
    label: $localize`Active cards`,
    value: CARD_STATUS.active,
  },
  {
    label: $localize`Frozen cards`,
    value: CARD_STATUS.suspended,
  },
];

export const CARD_STATUS_DROPDOWN_V2 = [
  {
    label: $localize`All cards`,
    key: `${CARD_STATUS.active},${CARD_STATUS.active_locked},${CARD_STATUS.suspended}`,
  },
  {
    label: $localize`Active cards`,
    key: CARD_STATUS.active,
  },
  {
    label: $localize`Locked cards`,
    key: CARD_STATUS.active_locked,
  },
  {
    label: $localize`Frozen cards`,
    key: CARD_STATUS.suspended,
  },
  {
    label: $localize`Deactivated cards`,
    key: CARD_STATUS.terminated,
  },
];

export const CARD_STATUS_DROPDOWN_V2_PHX = [
  {
    label: $localize`Active`,
    value: CARD_STATUS.active,
  },
  {
    label: $localize`Locked`,
    value: CARD_STATUS.active_locked,
  },
  {
    label: $localize`Frozen`,
    value: CARD_STATUS.suspended,
  },
];

export const CARD_STATUS_DROPDOWN_V3_PHX = [
  {
    label: $localize`Active`,
    value: CARD_STATUS.active,
  },
  {
    label: $localize`Locked`,
    value: CARD_STATUS.active_locked,
  },
  {
    label: $localize`Frozen`,
    value: CARD_STATUS.suspended,
  },
  {
    label: $localize`Unassigned`,
    value: CARD_STATUS.unassigned,
  },
];

export const UPSELL_FEATURE_KEYS = {
  ADMIN: 'fleet_card_upsell_admin',
  ADMIN_APPLICATION_STARTED: 'fleet_card_upsell_admin_application_started',
  USER: 'fleet_card_upsell_user',
  EOQ: 'fg-eoq-upsell-q1-2023',
};

export const OVERVIEW_DEFAULT_DATE_RANGE = 7;

export const OVERVIEW_DATE_RANGES = [OVERVIEW_DEFAULT_DATE_RANGE, 14, 28];

export const SPENDING_BREAKDOWN_DEFAULT_COLOR = BLUE_30;

export const SPENDING_BREAKDOWN_COLORS = {
  DIESEL: BLUE_90,
  GASOLINE: BLUE_70,
  'MISC FUEL': BLUE_50,
  OTHERS: SPENDING_BREAKDOWN_DEFAULT_COLOR,
};

export const SPENDING_BREAKDOWN_KEYS = {
  MISC: 'misc',
};

export const SPENDING_BREAKDOWN_COLORSV2 = [BLUE_90, BLUE_70, BLUE_50, SPENDING_BREAKDOWN_DEFAULT_COLOR];

export const SPENDING_BREAKDOWN_COLORSV3 = [BLUE_60, BLUE_30, YELLOW_20, GREY_20];

export const FUEL_MCCS = ['5541', '5983', '5542', 'QAIR8TC1R2NZRWN'];
export const FLEET_MCCS = ['4468', '4784', '5013', '5511', '5531', '5532', '5533', '5599', '7531', '7534', '7535',
  '7538', '7542', '7549', '7692', '7699', '7523', '7524'];
export const VEHICLE_MATCHING_MCCS = FUEL_MCCS.concat(FLEET_MCCS);
export const MAINTENANCE_MCCS = ['5533','5511','7538','5013','7531','7699','7692'];

export const FLEET_VIEW_TRANSACTION_TIMEOUT = 10000;

export const TRANSACTION_CATEGORY_OPTIONS_V3: PhxSelectOption[] = [
  {
    label: $localize`Gasoline`,
    value: CATEGORIES_FILTER_KEYS.gasoline,
  },
  {
    label: $localize`Diesel`,
    value: CATEGORIES_FILTER_KEYS.diesel,
  },
  {
    label: $localize`Diesel Exhaust Fuel (DEF)`,
    value: CATEGORIES_FILTER_KEYS.def,
  },
  {
    label: $localize`Reefer fuel`,
    value: CATEGORIES_FILTER_KEYS.reefer,
  },
  {
    label: $localize`Misc fuel`,
    value: CATEGORIES_FILTER_KEYS.miscFuel,
  },
  {
    label: $localize`Car washes`,
    value: CATEGORIES_FILTER_KEYS.carWashes,
  },
  {
    label: $localize`Cash`,
    value: CATEGORIES_FILTER_KEYS.cash,
  },
  {
    label: $localize`Food`,
    value: CATEGORIES_FILTER_KEYS.food,
  },
  {
    label: $localize`Maintenance`,
    value: CATEGORIES_FILTER_KEYS.maintenance,
  },
  {
    label: $localize`Parking & tolls`,
    value: CATEGORIES_FILTER_KEYS.parkingAndTolls,
  },
  {
    label: $localize`Tires`,
    value: CATEGORIES_FILTER_KEYS.tires,
  },
  {
    label: $localize`Towing`,
    value: CATEGORIES_FILTER_KEYS.towing,
  },
  {
    label: $localize`Travel`,
    value: CATEGORIES_FILTER_KEYS.travel,
  },
  {
    label: $localize`Misc.`,
    value: CATEGORIES_FILTER_KEYS.misc,
  },
  {
    label: $localize`Monthly Fee`,
    value: FEE_TYPES_KEYS.monthly_fee,
  },
  {
    label: $localize`Late Fee`,
    value: FEE_TYPES_KEYS.late_fee,
  },
  {
    label: MERCHANT_DISCOUNT_CATEGORY,
    value: CATEGORIES_FILTER_KEYS.merchantDiscount,
  }
];

export const TRANSACTION_CATEGORY_OPTIONS_V3_MAPPING: { [key: string]: string } = {
  [CATEGORIES_FILTER_KEYS.def]: $localize`Diesel Exhaust Fuel (DEF)`,
  [CATEGORIES_FILTER_KEYS.reefer]: $localize`Reefer fuel`,
};

export const TRANSACTION_CATEGORY_OPTIONS_V4: PhxSelectOption[] = [
  {
    groupLabel : $localize`Frequently purchased`,
  },
  {
    label: $localize`Diesel`,
    value: CATEGORIES_FILTER_KEYS.diesel,
  },
  {
    label: $localize`Gasoline`,
    value: CATEGORIES_FILTER_KEYS.gasoline,
  },
  {
    label: $localize`Maintenance`,
    value: CATEGORIES_FILTER_KEYS.maintenance,
  },
  {
    groupLabel : $localize`Other`,
  },
  {
    label: $localize`Advertising`,
    value: CATEGORIES_FILTER_KEYS.advertising,
  },
  {
    label: $localize`Air travel`,
    value: CATEGORIES_FILTER_KEYS.airTravel,
  },
  {
    label: $localize`Books & newspapers`,
    value: CATEGORIES_FILTER_KEYS.booksAndnewspapers,
  },
  {
    label: $localize`Buses and trains`,
    value: CATEGORIES_FILTER_KEYS.busesAndTrains,
  },
  {
    label: $localize`Business & professional services`,
    value: CATEGORIES_FILTER_KEYS.businessAndprofessionalServices,
  },
  {
    label: $localize`Car rentals`,
    value: CATEGORIES_FILTER_KEYS.carrRentals,
  },
  {
    label: $localize`Car washes`,
    value: CATEGORIES_FILTER_KEYS.carWashes,
  },
  {
    label: $localize`Cash`,
    value: CATEGORIES_FILTER_KEYS.cash,
  },
  {
    label: $localize`Clubs & memberships`,
    value: CATEGORIES_FILTER_KEYS.clubsAndmemberships,
  },
  {
    label: $localize`Computer hardware and software`,
    value: CATEGORIES_FILTER_KEYS.computerHardwareAndSoftware,
  },
  {
    label: $localize`Diesel Exhaust Fuel (DEF)`,
    value: CATEGORIES_FILTER_KEYS.def,
  },
  {
    label: $localize`Electric vehicle charging`,
    value: CATEGORIES_FILTER_KEYS.electricVehicleCharging,
  },
  {
    label: $localize`Entertainment`,
    value: CATEGORIES_FILTER_KEYS.entertainment,
  },
  {
    label: $localize`Food`,
    value: CATEGORIES_FILTER_KEYS.food,
  },
  {
    label: $localize`Fuel station purchases`,
    value: CATEGORIES_FILTER_KEYS.fuelStationPurchases,
  },
  {
    label: $localize`Government services`,
    value: CATEGORIES_FILTER_KEYS.governmentServices,
  },
  {
    label: $localize`Hardware stores`,
    value: CATEGORIES_FILTER_KEYS.hardwareStores,
  },
  {
    label: $localize`Insurance`,
    value: CATEGORIES_FILTER_KEYS.insurance,
  },
  {
    label: $localize`Internet & phone services`,
    value: CATEGORIES_FILTER_KEYS.internetAndPhoneServices,
  },
  {
    label: $localize`Late Fee`,
    value: FEE_TYPES_KEYS.late_fee,
  },
  {
    label: $localize`Lodging`,
    value: CATEGORIES_FILTER_KEYS.lodging,
  },
  {
    label: $localize`medical`,
    value: CATEGORIES_FILTER_KEYS.medical,
  },
  {
    label: $localize`Misc. (non-fuel)`,
    value: CATEGORIES_FILTER_KEYS.miscNonFuel,
  },
  {
    label: $localize`Misc. fuel`,
    value: CATEGORIES_FILTER_KEYS.miscFuel,
  },
  {
    label: $localize`Monthly Fee`,
    value: FEE_TYPES_KEYS.monthly_fee,
  },
  {
    label: $localize`Office supplies`,
    value: CATEGORIES_FILTER_KEYS.officeSupplies,
  },
  {
    label: $localize`Travel expenses`,
    value: CATEGORIES_FILTER_KEYS.travelExpenses,
  },
  {
    label: $localize`Parking & tolls`,
    value: CATEGORIES_FILTER_KEYS.parkingAndTolls,
  },
  {
    label: $localize`Reefer fuel`,
    value: CATEGORIES_FILTER_KEYS.reefer,
  },
  {
    label: $localize`Supermarkets & general merchandise`,
    value: CATEGORIES_FILTER_KEYS.supermarketsAndGeneralMerchandise,
  },
  {
    label: $localize`Taxis and rideshares`,
    value: CATEGORIES_FILTER_KEYS.taxisAndRideshares,
  },
  {
    label: $localize`Tires`,
    value: CATEGORIES_FILTER_KEYS.tires,
  },
  {
    label: $localize`Towing`,
    value: CATEGORIES_FILTER_KEYS.towing,
  },
  {
    label: $localize`Training and education`,
    value: CATEGORIES_FILTER_KEYS.trainingAndEducation,
  },
  {
    label: $localize`Utilities (electric, gas, etc.)`,
    value: CATEGORIES_FILTER_KEYS.utilities,
  },
  {
    label: MERCHANT_DISCOUNT_CATEGORY,
    value: CATEGORIES_FILTER_KEYS.merchantDiscount,
  }
];

export const DISTANCE_UNITS = {
  MI: 'mi',
  KM: 'km',
};

export const CAPACITY_UNITS = {
  GALLON: 'gallon',
  GALLON_ABBR: 'gal',
  LITER: 'liter',
  LITER_ABBR: 'L',
};

export const CAPACITY_UNITS_MAX_VALUE = {
  GALLON: 500,
  LITER: 2000,
};

export const valuePropsDiscountPartnersText = (count: string = '35,000') => {
  return $localize`We have over ${count} partner locations and growing, including Love’s, TA, Road Ranger and 7-Eleven. No minimum gallons required.`;
};

export const markAsReviewedActions = {
  incorrectly_flagged: 'incorrectly_flagged',
  irregularity_confirmed: 'irregularity_confirmed',
  vehicle_fuel_type_incorrect: 'vehicle_fuel_type_incorrect',
  vehicle_id_incorrect: 'vehicle_id_incorrect',
  asset_purchase: 'asset_purchase',
  vehicle_tank_capacity_incorrect: 'vehicle_tank_capacity_incorrect',
  vehicle_or_asset_id_incorrect: 'vehicle_or_asset_id_incorrect',
};

export const REPORT_ISSUE_DEFAULT_OPTIONS = {
  incorrectVehicleOrAssetAssign: 'incorrectVehicleOrAssetAssign',
  incorrectVehicleAssign: 'incorrectVehicleAssign',
  incorrectAssignedFuel: 'incorrectAssignedFuel',
  incorrectPurchaseFuel: 'incorrectPurchaseFuel',
  purchasedForAnAsset: 'purchasedForAnAsset',
} as const;

export const REPORT_ISSUE_FUEL_LEVEL_SPEND_MISMATCH_OPTIONS = {
  incorrectVehicleAssign: 'incorrectVehicleAssign',
  incorrectVehicleOrAssetAssign: 'incorrectVehicleOrAssetAssign',
  incorrectFuelTankCapacity: 'incorrectFuelTankCapacity',
  fuelPurchasedForAnAsset: 'fuelPurchasedForAnAsset',
};

type reportIssueDefaultOptionKeys = keyof typeof REPORT_ISSUE_DEFAULT_OPTIONS;
export type reportIssueDefaultOptionsType = typeof REPORT_ISSUE_DEFAULT_OPTIONS[reportIssueDefaultOptionKeys];

export const irregularityReasonsKeys = {
  fuelTankLevelMismatch: 'fuel_tank_level_mismatch',
  fuelLevelExceedsLimit: 'fuel_level_exceeds_limit',
  vehicleLocationMismatch: 'vehicle_location_mismatch',
  vehicleProximityMismatch: 'vehicle_proximity_mismatch',
  fuelTypeMismatch: 'fuel_type_mismatch',
  reviewableFraudSuspected: 'reviewable_fraud_suspected',
  reviewableFraudActivity: 'reviewable_fraud_activity',
  softDeclineReviewRequired: 'soft_decline_review_required',
};

export const FUEL_TANK_LEVEL_GRAPH_LABEL = {
  BEFORE: $localize`Before`,
  AFTER: $localize`After`,
  ACTUAL: $localize`Actual`,
  EXPECTED: $localize`Expected`,
};

export const FUEL_TANK_FLAG_DECIMAL_PRECISION = '1.0-2';
export const TRANSACTION_LIST_TABLE_DATE_FORMAT: string = 'MM/DD/YYYY';
export const TRANSACTION_LIST_ITEM_NOT_AVAILABLE_TEXT: string = 'N/A';

export const NEW_CATEGORY_ROW_VALUE: ICategoryRow = {
  quantity: null,
  unitPrice: null,
  grossAmount: null,
  productType: null,
  unitOfMeasure: null,
  allowNegative: false,
};

export const FUEL_CATEGORIES: string[] = [
  CATEGORIES_FILTER_KEYS.gasoline,
  CATEGORIES_FILTER_KEYS.diesel,
  CATEGORIES_FILTER_KEYS.miscFuel,
  CATEGORIES_FILTER_KEYS.def,
  CATEGORIES_FILTER_KEYS.reefer,
];

export const FUEL_CATEGORIES_LABELS: string[] = [
  'Gasoline',
  'Diesel',
  'Misc fuel',
  'Diesel Exhaust Fuel (DEF)',
  'Reefer fuel',
];

export const NON_EDITABLE_CATEGORIES: string[] = [
  CATEGORIES_FILTER_KEYS.merchantDiscount,
];

export const NON_EDITABLE_CATEGORIES_LABEL: string[] = [
  MERCHANT_DISCOUNT_CATEGORY
];

export const ENTITY_TYPE = {
  ASSET: 'asset',
  DRIVER: 'driver',
  VEHICLE: 'vehicle',
  FLEET_USER: 'fleet_user',
};

export type entityType = 'asset' | 'driver' | 'vehicle' | 'fleet_user';

export const ENTITY_STATE_ICONS = {
  [EntityState.MOVING]: '/map/moving-street.svg',
  [EntityState.OFF]: '/map/stationary-street.svg',
  [EntityState.IDLING]: '/map/idle-street.svg',
};

export const IRREGULARITY_REASONS_MAP = {
  vehicle_location_mismatch: $localize`Vehicle & spend location mismatch`,
  fuel_type_mismatch: $localize`Fuel Type Mismatch`,
  vehicle_proximity_mismatch: $localize`Vehicle & spend location mismatch`,
  fuel_tank_level_mismatch: $localize`Fuel Level & Spend Mismatch`,
  soft_decline_review_required: $localize`Soft decline review required`,
};

export const VG_STATUS = {
  OUTDATED: 'outdated',
  AVAILABLE: 'available',
  UNRELIABLE: 'unreliable',
  UNAVAILABLE: 'unavailable',
} as const;

export type vgStatusType = typeof VG_STATUS[keyof typeof VG_STATUS];

export const REPORT_ISSUE_MODAL_HEADER_TITLE = {
  UPDATE_VEHICLE_FUEL_TYPE: 'UPDATE_VEHICLE_FUEL_TYPE',
};

export const CARDHOLDER_TYPES = {
  FLEET_USER: 'fleet_user',
  DRIVER: 'driver',
  PERSON: 'person',
} as const;

export type cardholderType = typeof CARDHOLDER_TYPES[keyof typeof CARDHOLDER_TYPES];

export const SEARCH_DEBOUNCE_TIME_IN_MS: number = 400;

export enum SettingSkipReasonKeys {
  NON_FUEL_TXN = 'non_fuel_txn',
  VEHICLE_ID_NOT_PRESENT = 'vehicle_id_not_present',
  VG_MISSING = 'vg_missing',
  VG_STALE_DATA = 'vg_stale_data',
  UNRELIABLE_FUEL_INFO = 'unreliable_fuel_info',
  NO_FUEL_INFO = 'no_fuel_info',
  NON_FUEL_STATION_TXN = 'non_fuel_station_txn',
  QUIK_Q = 'quik_q',
}